import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  getUserById,
  loginService,
  verifyOtpService,
} from "../../services/services";

const baseUrl = process.env.REACT_APP_API_URL;

const safeJSONParse = (key) => {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : null;
  } catch (error) {
    console.error(`Failed to parse ${key} from localStorage:`, error);
    localStorage.removeItem(key);
    return null;
  }
};

const initialState = {
  user: safeJSONParse("Login_User"),
  token: localStorage.getItem("User_Token") || null,
  loading: false,
  freshUser: null,
  error: null,
  loginSuccess: false,
  otpSent: false,
  redirectTo: null,
  hasPin: false,
  hasBusiness: false,
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await loginService(credentials);
      const data = response.data;

      if (!data.status) {
        return rejectWithValue(data.message || "Failed to send OTP");
      }
      return data.response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send OTP",
      );
    }
  },
);

// Verify OTP
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      const response = await verifyOtpService({ phone, otp });
      if (!response.data.status) {
        return rejectWithValue(response.data.message || "OTP not matched");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || { message: "OTP verification failed" },
      );
    }
  },
);

export const fetchUser = createAsyncThunk(
  "auth/fetchUser",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await getUserById(userId);
      return res.data.response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.loginSuccess = false;
      state.freshUser = null;
      state.error = null;

      state.otpSent = false;
      state.redirectTo = null;
      state.hasPin = false;
      state.hasBusiness = false;

      localStorage.removeItem("User_Token");
      localStorage.removeItem("Login_User");
      localStorage.removeItem("selectedBusiness");

      delete axios.defaults.headers.common["Authorization"];
    },
    resetLoginSuccess: (state) => {
      state.loginSuccess = false;
      state.redirectTo = null;
    },
    setUserAndToken: (state, action) => {
      const { user, token } = action.payload;
      state.user = user;
      state.token = token;
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    },
  },
  extraReducers: (builder) => {
    builder
      // LOGIN (send OTP)
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.otpSent = false;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;

        const payload = action.payload || {};
        const forceOtp = action.meta.arg.forceOtp;

        if (forceOtp) {
          state.otpSent = true;
          state.redirectTo = null;
          return;
        }
        if (payload.hasPin) {
          state.redirectTo = "pin";
          state.otpSent = false;
        } else if (!payload.hasPin && payload.hasBusiness) {
          state.redirectTo = "createPin";
        } else {
          state.otpSent = true;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.otpSent = false;
      })

      // VERIFY OTP
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.loginSuccess = true;
        state.error = null;

        const payload = action.payload;
        const data = payload?.response || payload;

        state.user = data?.user || null;
        state.token = data?.token || null;
        state.hasPin = data.hasPin;
        state.hasBusiness = data.hasBusiness;

        if (state.token) {
          localStorage.setItem("User_Token", state.token);
          if (state.user) {
            localStorage.setItem("Login_User", JSON.stringify(state.user));
          }
          localStorage.removeItem("selectedBusiness");
          axios.defaults.headers.common["Authorization"] =
            `Bearer ${state.token}`;
        }

        const resetPinFlow = action.meta.arg.resetPinFlow;

        if (resetPinFlow) {
          state.redirectTo = "createPin";
        } else if (!data.hasPin && data.hasBusiness) {
          state.redirectTo = "createPin";
        } else if (data.hasPin) {
          state.redirectTo = "pin";
        } else {
          state.redirectTo = "business";
        }
        // state.redirectTo = payload?.redirectTo || "/dashboard";
        // state.redirectTo = null;
      })

      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.freshUser = action.payload;
      });
  },
});

export const { logout, resetLoginSuccess, setUserAndToken } = authSlice.actions;
export default authSlice.reducer;
